import {
  CognitoIdentityProviderClient,
  GetTokensFromRefreshTokenCommand,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  RevokeTokenCommand,
} from '@aws-sdk/client-cognito-identity-provider'
import { clearSession, loadSession, mergeRefreshedSession, saveSession, sessionFromAuthResult } from './tokenStore'
import { uiBuild, uiEnv } from './uiMeta'

const REGION = import.meta.env.VITE_AWS_REGION || 'us-east-1'
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID
const USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID

const client = new CognitoIdentityProviderClient({ region: REGION })

export function toE164(phone) {
  const digits = String(phone || '').replace(/\D/g, '')
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  if (national.length !== 10) {
    throw new Error('Phone must be 10 digits')
  }
  return `+1${national}`
}

export function toNational(phone) {
  const digits = String(phone || '').replace(/\D/g, '')
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  return national
}

function requireClientId() {
  if (!CLIENT_ID) {
    throw new Error('VITE_COGNITO_CLIENT_ID is not configured')
  }
  return CLIENT_ID
}

export function cognitoConfigured() {
  return Boolean(CLIENT_ID && USER_POOL_ID)
}

export function newAttemptId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `attempt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

export async function initiateCustomAuth(phone, method = 'sms', attemptId = newAttemptId()) {
  const username = toE164(phone)
  const response = await client.send(
    new InitiateAuthCommand({
      AuthFlow: 'CUSTOM_AUTH',
      ClientId: requireClientId(),
      AuthParameters: { USERNAME: username },
      ClientMetadata: {
        method: method === 'call' ? 'call' : 'sms',
        attemptId,
        ui_env: uiEnv(),
        ui_build: uiBuild(),
      },
    })
  )
  return {
    session: response.Session,
    challengeName: response.ChallengeName,
    username,
    attemptId,
  }
}

export async function respondToAuthChallenge(phone, session, code, attemptId) {
  const username = toE164(phone)
  const response = await client.send(
    new RespondToAuthChallengeCommand({
      ClientId: requireClientId(),
      ChallengeName: 'CUSTOM_CHALLENGE',
      Session: session,
      ChallengeResponses: {
        USERNAME: username,
        ANSWER: String(code),
      },
      ClientMetadata: attemptId
        ? { attemptId, ui_env: uiEnv(), ui_build: uiBuild() }
        : { ui_env: uiEnv(), ui_build: uiBuild() },
    })
  )
  if (response.AuthenticationResult) {
    const stored = sessionFromAuthResult(response.AuthenticationResult)
    saveSession(stored)
    return { tokens: stored, session: null, challengeName: null }
  }
  return {
    tokens: null,
    session: response.Session,
    challengeName: response.ChallengeName,
  }
}

export async function refreshTokens() {
  const current = loadSession()
  if (!current?.refreshToken) {
    throw new Error('No refresh token')
  }
  const response = await client.send(
    new GetTokensFromRefreshTokenCommand({
      ClientId: requireClientId(),
      RefreshToken: current.refreshToken,
    })
  )
  const stored = mergeRefreshedSession(current, response.AuthenticationResult || {})
  saveSession(stored)
  return stored
}

export async function revokeCurrentSession() {
  const current = loadSession()
  if (current?.refreshToken && CLIENT_ID) {
    try {
      await client.send(
        new RevokeTokenCommand({
          ClientId: CLIENT_ID,
          Token: current.refreshToken,
        })
      )
    } catch (error) {
      console.warn('RevokeToken failed:', error)
    }
  }
  clearSession()
}

export function isRefreshAuthError(error) {
  const name = error?.name || error?.__type || ''
  return (
    name === 'NotAuthorizedException' ||
    name === 'RefreshTokenReuseException' ||
    String(name).includes('NotAuthorized') ||
    String(name).includes('RefreshTokenReuse')
  )
}
